import { lazy } from 'react';

export interface RouteEntry {
  path: string;
  component: ReturnType<typeof lazy>;
  protected?: boolean;
  requireAdmin?: boolean;
  /** If true, route is inside AppShell layout */
  shell?: boolean;
}

/* ── Public routes (no shell, no auth) ─────────────────────────── */
const Auth = lazy(() => import('@/pages/Auth'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const Install = lazy(() => import('@/pages/Install'));
const Demo = lazy(() => import('@/pages/Demo'));
const Landing = lazy(() => import('@/pages/Landing'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('@/pages/TermsOfService'));
const StatusPage = lazy(() => import('@/pages/StatusPage'));
const SecurityPolicy = lazy(() => import('@/pages/SecurityPolicy'));
const Unsubscribe = lazy(() => import('@/pages/Unsubscribe'));
const MobileCompanion = lazy(() => import('@/pages/MobileCompanion'));
const SLAWDetection = lazy(() => import('@/pages/SLAWDetection'));
const SLAWGov = lazy(() => import('@/pages/SLAWGov'));
const SLAWIndex = lazy(() => import('@/pages/SLAWIndex'));
const SLAWLanding = lazy(() => import('@/pages/SLAWLanding'));
const SLAWMethodology = lazy(() => import('@/pages/SLAWMethodology'));
const GovContractsPublic = lazy(() => import('@/pages/GovContracts'));
const SLAWReportSuccess = lazy(() => import('@/pages/SLAWReportSuccess'));
const SLAWReportRecovery = lazy(() => import('@/pages/SLAWReportRecovery'));
const MyReports = lazy(() => import('@/pages/MyReports'));
const IntelligenceBriefing = lazy(() => import('@/pages/IntelligenceBriefing'));
const AwardIntel = lazy(() => import('@/pages/AwardIntel'));
const ContractDNA = lazy(() => import('@/pages/ContractDNA'));
const ExclusionWatch = lazy(() => import('@/pages/ExclusionWatch'));
const OpportunityRadar = lazy(() => import('@/pages/OpportunityRadar'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const Pulse = lazy(() => import('@/pages/Pulse'));
const MethodologyAccuracy = lazy(() => import('@/pages/MethodologyAccuracy'));
const MethodologyTrust = lazy(() => import('@/pages/MethodologyTrust'));
const GovReportsCatalog = lazy(() => import('@/pages/GovReportsCatalog'));
const GovReceipt = lazy(() => import('@/pages/GovReceipt'));
const PricingPublic = lazy(() => import('@/pages/Pricing'));
const ROICalculatorPublic = lazy(() => import('@/pages/ROICalculator'));
const DoDTickerWidget = lazy(() => import('@/pages/DoDTickerWidget'));
const WidgetMarketing = lazy(() => import('@/pages/WidgetMarketing'));
const PartnerTracker = lazy(() => import('@/pages/PartnerTracker'));
const OgPreview = lazy(() => import('@/pages/OgPreview'));
const DoDAwardsFeedRoute = lazy(() => import('@/pages/DoDAwardsFeedRoute'));
const DoDOgRedirect = lazy(() => import('@/pages/DoDOgRedirect'));
const Disclaimers = lazy(() => import('@/pages/Disclaimers'));
const AcceptableUsePolicy = lazy(() => import('@/pages/AcceptableUsePolicy'));
const About = lazy(() => import('@/pages/About'));
const CrmCleanse = lazy(() => import('@/pages/CrmCleanse'));

export const PUBLIC_ROUTES: RouteEntry[] = [
  { path: '/auth', component: Auth },
  { path: '/reset-password', component: ResetPassword },
  { path: '/install', component: Install },
  { path: '/demo', component: Demo },
  { path: '/', component: Landing },
  { path: '/privacy-policy', component: PrivacyPolicy },
  { path: '/terms', component: TermsOfService },
  { path: '/status', component: StatusPage },
  { path: '/security-policy', component: SecurityPolicy },
  { path: '/unsubscribe', component: Unsubscribe },
  { path: '/companion', component: MobileCompanion },
  { path: '/landing', component: Landing },
  { path: '/pulse', component: Pulse },
  { path: '/methodology/accuracy', component: MethodologyAccuracy },
  { path: '/methodology/trust', component: MethodologyTrust },
  { path: '/widget', component: WidgetMarketing },
  { path: '/widget/dod-ticker', component: DoDTickerWidget },
  { path: '/partner-tracker', component: PartnerTracker },
  { path: '/og-preview', component: OgPreview },
  { path: '/api/dod/awards.json', component: DoDAwardsFeedRoute },
  { path: '/api/dod/awards.rss', component: DoDAwardsFeedRoute },
  { path: '/og/dod/today', component: DoDOgRedirect },
  { path: '/og/dod/award/:id', component: DoDOgRedirect },
  { path: '/disclaimers', component: Disclaimers },
  { path: '/acceptable-use', component: AcceptableUsePolicy },
  { path: '/about', component: About },
  { path: '/crm-cleanse', component: CrmCleanse },
];

/* ── SLAW routes (inside SLAWShell layout, public) ────────────── */
export const SLAW_ROUTES: RouteEntry[] = [
  { path: '/slaw-landing', component: SLAWLanding },
  { path: '/methodology', component: SLAWMethodology },
  { path: '/slaw-index', component: SLAWIndex },
  { path: '/slaw-detection', component: SLAWDetection },
  { path: '/slaw-gov', component: SLAWGov },
  { path: '/slaw-report-success', component: SLAWReportSuccess },
  { path: '/slaw-report-recovery', component: SLAWReportRecovery },
  { path: '/my-reports', component: MyReports },
  { path: '/contracts', component: GovContractsPublic },
  { path: '/intelligence', component: IntelligenceBriefing },
  { path: '/award-intel', component: AwardIntel },
  { path: '/contract-dna', component: ContractDNA },
  { path: '/exclusion-watch', component: ExclusionWatch },
  { path: '/opportunity-radar', component: OpportunityRadar },
  { path: '/reports', component: GovReportsCatalog },
  { path: '/reports/receipt/:publicId', component: GovReceipt },
  { path: '/pricing', component: PricingPublic },
  { path: '/roi-calculator', component: ROICalculatorPublic },
];

/* ── Shell routes (inside AppShell, protected unless noted) ───── */
const Home = lazy(() => import('@/pages/Home'));
const Index = lazy(() => import('@/pages/Index'));
const SavedMappings = lazy(() => import('@/pages/SavedMappings'));
const SchemaGraph = lazy(() => import('@/pages/SchemaGraph'));
const ExecutionObservability = lazy(() => import('@/pages/ExecutionObservability'));
const MappingHealth = lazy(() => import('@/pages/MappingHealth'));
const CanonicalLayer = lazy(() => import('@/pages/CanonicalLayer'));
const AgencyPortal = lazy(() => import('@/pages/AgencyPortal'));
const RulesEngine = lazy(() => import('@/pages/RulesEngine'));
const DecisionEngine = lazy(() => import('@/pages/DecisionEngine'));
const ROIDashboard = lazy(() => import('@/pages/ROIDashboard'));
const DualDashboard = lazy(() => import('@/pages/DualDashboard'));
const UsageDashboard = lazy(() => import('@/pages/UsageDashboard'));
const AdminPanel = lazy(() => import('@/pages/AdminPanel'));
const AIGovernance = lazy(() => import('@/pages/AIGovernance'));
const AdoptionDashboard = lazy(() => import('@/pages/AdoptionDashboard'));
const TeamLeaderboard = lazy(() => import('@/pages/TeamLeaderboard'));
const RideAlong = lazy(() => import('@/pages/RideAlong'));
const CorrectionsQueue = lazy(() => import('@/pages/CorrectionsQueue'));
const ObjectionLibrary = lazy(() => import('@/pages/ObjectionLibrary'));
const CampaignManager = lazy(() => import('@/pages/CampaignManager'));
const LaunchTracker = lazy(() => import('@/pages/LaunchTracker'));
const BrandCenter = lazy(() => import('@/pages/BrandCenter'));
const SalesFunnel = lazy(() => import('@/pages/SalesFunnel'));
const CrmConnections = lazy(() => import('@/pages/CrmConnections'));
const PrivacySettings = lazy(() => import('@/pages/PrivacySettings'));
const Webhooks = lazy(() => import('@/pages/Webhooks'));
const TeamManagement = lazy(() => import('@/pages/TeamManagement'));
const ApiKeys = lazy(() => import('@/pages/ApiKeys'));
const Documentation = lazy(() => import('@/pages/Documentation'));
const IssueTracker = lazy(() => import('@/pages/IssueTracker'));
const ModelGovernance = lazy(() => import('@/pages/ModelGovernance'));
const BrandShowcase = lazy(() => import('@/pages/BrandShowcase'));
const BrainAnalytics = lazy(() => import('@/pages/BrainAnalytics'));
const DLQViewer = lazy(() => import('@/pages/DLQViewer'));
const IntegrationAsCode = lazy(() => import('@/pages/IntegrationAsCode'));
const TraceExport = lazy(() => import('@/pages/TraceExport'));
const PredictiveDrift = lazy(() => import('@/pages/PredictiveDrift'));
const ConnectorSDK = lazy(() => import('@/pages/ConnectorSDK'));
const TransformSandbox = lazy(() => import('@/pages/TransformSandbox'));
const SecurityDashboard = lazy(() => import('@/pages/SecurityDashboard'));
const DataLineage = lazy(() => import('@/pages/DataLineage'));
const QualityDashboard = lazy(() => import('@/pages/QualityDashboard'));
const PatternLibrary = lazy(() => import('@/pages/PatternLibrary'));
const EfficiencyDashboard = lazy(() => import('@/pages/EfficiencyDashboard'));
const CentralizedLogs = lazy(() => import('@/pages/CentralizedLogs'));
const CostManagement = lazy(() => import('@/pages/CostManagement'));
const SolutionTemplates = lazy(() => import('@/pages/SolutionTemplates'));
const AnomalyDetection = lazy(() => import('@/pages/AnomalyDetection'));
const BulkImportExport = lazy(() => import('@/pages/BulkImportExport'));
const DataSteward = lazy(() => import('@/pages/DataSteward'));
const NetworkLearningSettings = lazy(() => import('@/pages/NetworkLearningSettings'));
const ComplianceEvidence = lazy(() => import('@/pages/ComplianceEvidence'));
const DeveloperPortal = lazy(() => import('@/pages/DeveloperPortal'));
const PlatformHub = lazy(() => import('@/pages/PlatformHub'));
const PocReportGenerator = lazy(() => import('@/pages/PocReportGenerator'));
const WaitlistAdmin = lazy(() => import('@/pages/WaitlistAdmin'));
const GovContracts = lazy(() => import('@/pages/GovContracts'));
const SLAWMonitor = lazy(() => import('@/pages/SLAWMonitor'));
const DataValidation = lazy(() => import('@/pages/DataValidation'));
const DemoWalkthrough = lazy(() => import('@/pages/DemoWalkthrough'));
const RetentionDashboard = lazy(() => import('@/pages/RetentionDashboard'));
const SamApiHealth = lazy(() => import('@/pages/SamApiHealth'));
const UATRunner = lazy(() => import('@/pages/UATRunner'));
const UATResults = lazy(() => import('@/pages/UATResults'));
const GovReportCacheAdmin = lazy(() => import('@/pages/GovReportCacheAdmin'));

export const SHELL_ROUTES: RouteEntry[] = [
  // Core
  { path: '/home', component: Home, protected: true, shell: true },
  { path: '/studio', component: Index, protected: true, shell: true },
  { path: '/mappings', component: SavedMappings, protected: true, shell: true },
  { path: '/graph', component: SchemaGraph, protected: true, shell: true },
  { path: '/observability', component: ExecutionObservability, protected: true, shell: true },
  { path: '/health', component: MappingHealth, protected: true, shell: true },
  { path: '/canonical', component: CanonicalLayer, protected: true, shell: true },

  // Admin
  { path: '/agency', component: AgencyPortal, protected: true, requireAdmin: true, shell: true },
  { path: '/admin', component: AdminPanel, protected: true, requireAdmin: true, shell: true },
  { path: '/brain-analytics', component: BrainAnalytics, protected: true, requireAdmin: true, shell: true },
  { path: '/admin/retention', component: RetentionDashboard, protected: true, requireAdmin: true, shell: true },
  { path: '/admin/sam-api-health', component: SamApiHealth, protected: true, requireAdmin: true, shell: true },
  { path: '/admin/uat', component: UATRunner, protected: true, requireAdmin: true, shell: true },
  { path: '/admin/uat-results', component: UATResults, protected: true, requireAdmin: true, shell: true },
  { path: '/admin/report-cache', component: GovReportCacheAdmin, protected: true, requireAdmin: true, shell: true },

  // Intelligence
  { path: '/rules', component: RulesEngine, protected: true, shell: true },
  { path: '/engine', component: DecisionEngine, protected: true, shell: true },
  { path: '/ride-along', component: RideAlong, protected: true, shell: true },
  { path: '/corrections', component: CorrectionsQueue, protected: true, shell: true },
  { path: '/objections', component: ObjectionLibrary, protected: true, shell: true },

  // Analytics
  { path: '/dashboard', component: ROIDashboard, protected: true, shell: true },
  { path: '/dashboard/compare', component: DualDashboard, protected: true, shell: true },
  { path: '/usage', component: UsageDashboard, protected: true, shell: true },
  { path: '/governance', component: AIGovernance, protected: true, shell: true },
  { path: '/adoption', component: AdoptionDashboard, protected: true, shell: true },
  { path: '/leaderboard', component: TeamLeaderboard, protected: true, shell: true },

  // Public-ish (inside shell but no auth required)
  { path: '/brand-showcase', component: BrandShowcase, shell: true },

  // Revenue & Marketing
  { path: '/campaigns', component: CampaignManager, protected: true, shell: true },
  { path: '/launches', component: LaunchTracker, protected: true, shell: true },
  { path: '/brand', component: BrandCenter, protected: true, shell: true },
  { path: '/funnel', component: SalesFunnel, protected: true, shell: true },

  // Settings & Integrations
  { path: '/crm', component: CrmConnections, protected: true, shell: true },
  { path: '/privacy', component: PrivacySettings, protected: true, shell: true },
  { path: '/webhooks', component: Webhooks, protected: true, shell: true },
  { path: '/team', component: TeamManagement, protected: true, shell: true },
  { path: '/api-keys', component: ApiKeys, protected: true, shell: true },
  { path: '/docs', component: Documentation, protected: true, shell: true },
  { path: '/issues', component: IssueTracker, protected: true, shell: true },

  // Platform
  { path: '/model-governance', component: ModelGovernance, protected: true, shell: true },
  { path: '/dlq', component: DLQViewer, protected: true, shell: true },
  { path: '/iac', component: IntegrationAsCode, protected: true, shell: true },
  { path: '/traces', component: TraceExport, protected: true, shell: true },
  { path: '/predictive-drift', component: PredictiveDrift, protected: true, shell: true },
  { path: '/connector-sdk', component: ConnectorSDK, protected: true, shell: true },
  { path: '/transform-sandbox', component: TransformSandbox, protected: true, shell: true },
  { path: '/security', component: SecurityDashboard, protected: true, shell: true },
  { path: '/lineage', component: DataLineage, protected: true, shell: true },
  { path: '/quality', component: QualityDashboard, protected: true, shell: true },
  { path: '/patterns', component: PatternLibrary, protected: true, shell: true },
  { path: '/efficiency', component: EfficiencyDashboard, protected: true, shell: true },
  { path: '/logs', component: CentralizedLogs, protected: true, shell: true },
  { path: '/costs', component: CostManagement, protected: true, shell: true },
  { path: '/templates', component: SolutionTemplates, protected: true, shell: true },
  { path: '/anomalies', component: AnomalyDetection, protected: true, shell: true },
  { path: '/bulk', component: BulkImportExport, protected: true, shell: true },
  { path: '/steward', component: DataSteward, protected: true, shell: true },
  { path: '/network-learning', component: NetworkLearningSettings, protected: true, shell: true },
  { path: '/compliance', component: ComplianceEvidence, protected: true, shell: true },
  { path: '/developer', component: DeveloperPortal, protected: true, shell: true },
  { path: '/platform-hub', component: PlatformHub, protected: true, shell: true },
  { path: '/poc-report', component: PocReportGenerator, protected: true, shell: true },
  { path: '/waitlist-admin', component: WaitlistAdmin, protected: true, requireAdmin: true, shell: true },
  { path: '/gov-contracts', component: GovContracts, protected: true, shell: true },
  { path: '/slaw-monitor', component: SLAWMonitor, protected: true, shell: true },
  { path: '/data-validation', component: DataValidation, protected: true, shell: true },
  { path: '/demo-walkthrough', component: DemoWalkthrough, protected: true, shell: true },
];

export const CATCH_ALL_ROUTE: RouteEntry = {
  path: '*',
  component: NotFound,
};
